import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CONSUMER_FETCH_FAILED,
  fetchSingleConsumer,
  type ConsumerData,
} from '@/services/consumerService'
import { createLatestRequest } from '@/utils/latestRequest'
import type { GatewayError } from '@/utils/gatewayError'

export type ConsumerPremiseStatus = 'loading' | 'ready' | 'error'

export type ConsumerPremise = {
  status: ConsumerPremiseStatus
  premiseId: number | null
  consumer: ConsumerData | null
  error: GatewayError | null
  reload: () => void
}

export const MISSING_METER_NUMBER_ERROR: GatewayError = {
  message: 'מספר המד חסר בכתובת הדף',
}

export const MISSING_ACCOUNT_ERROR: GatewayError = {
  message: 'פרטי החשבון חסרים — התחברו מחדש',
}

export const MISSING_PREMISE_ERROR: GatewayError = {
  message: `${CONSUMER_FETCH_FAILED} — לא הוחזר מתחם (premise) למד זה`,
}

/**
 * Resolves the premise id of a meter number. Every tab of the meter page needs
 * it before it can ask for readings or alerts, and each tab resolves it on its
 * own: a tab that fails must not keep the other tabs waiting, and it has to
 * report the reason rather than spin.
 */
export const useConsumerPremise = (
  accountId: string | number | null | undefined,
  meterNumber: string | number | null | undefined
): ConsumerPremise => {
  const [state, setState] = useState<{
    status: ConsumerPremiseStatus
    premiseId: number | null
    consumer: ConsumerData | null
    error: GatewayError | null
  }>({ status: 'loading', premiseId: null, consumer: null, error: null })
  const [reloadToken, setReloadToken] = useState(0)
  const sequence = useRef({ current: 0 })

  const reload = useCallback(() => setReloadToken(token => token + 1), [])

  useEffect(() => {
    const failWith = (error: GatewayError) =>
      setState({ status: 'error', premiseId: null, consumer: null, error })

    if (
      meterNumber === null ||
      meterNumber === undefined ||
      meterNumber === ''
    ) {
      failWith(MISSING_METER_NUMBER_ERROR)
      return
    }
    if (accountId === null || accountId === undefined || accountId === '') {
      failWith(MISSING_ACCOUNT_ERROR)
      return
    }

    const request = createLatestRequest(sequence.current)
    setState({
      status: 'loading',
      premiseId: null,
      consumer: null,
      error: null,
    })

    fetchSingleConsumer(accountId, meterNumber).then(result => {
      if (!request.isCurrent()) return

      if (!result.ok) {
        failWith(result.error)
        return
      }
      if (!result.data.premiseId) {
        failWith(MISSING_PREMISE_ERROR)
        return
      }

      setState({
        status: 'ready',
        premiseId: result.data.premiseId,
        consumer: result.data,
        error: null,
      })
    })

    return () => request.cancel()
  }, [accountId, meterNumber, reloadToken])

  return { ...state, reload }
}
